import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * Lärarbutiken-logik:
 * - Vid sidladdning: byt INTE automatiskt till första variantens bild.
 * - När kunden aktivt klickar/väljer en variant: låt galleriet byta till variantens bild.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    /**
     * Viktigt:
     * Vi markerar bara variantbyte som aktivt när kunden faktiskt interagerar
     * med variantvalen. Då ignoreras automatisk variant-update vid sidladdning.
     */
    target?.addEventListener('change', this.#handleVariantInteraction, {
      signal,
      capture: true,
    });

    target?.addEventListener('click', this.#handleVariantInteraction, {
      signal,
      capture: true,
    });

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /**
   * Blir true först när kunden klickar/ändrar ett variantval.
   */
  #customerChangedVariant = false;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Känner av om användaren faktiskt interagerar med variantval.
   *
   * @param {Event} event
   */
  #handleVariantInteraction = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const isVariantControl =
      target.matches('input[name^="options"]') ||
      target.matches('select[name^="options"]') ||
      target.closest('variant-picker') ||
      target.closest('variant-selects') ||
      target.closest('variant-radios') ||
      target.closest('[data-option-value-id]') ||
      target.closest('[data-variant-id]');

    if (isVariantControl) {
      this.#customerChangedVariant = true;
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * Lärarbutiken:
   * Ignorera första automatiska variantuppdateringen vid sidladdning.
   * Tillåt byte först efter att kunden själv valt variant.
   *
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (!this.#customerChangedVariant) return;

    const source = event.detail.data.html;

    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}